import { useDispatch } from "react-redux";
import { ActionType, AppDispatch } from "../reducer";

// Records a fetch's outcome in the ui slice so a failed fetch ends its spinner. A failure is stored under `key` and the
// promise resolves undefined; a success clears any earlier failure and resolves the value. It never throws, so the call
// sites need no catch of their own and an abandoned fetch cannot raise an unhandled rejection.
export async function trackLoad<T>(
  dispatch: AppDispatch,
  key: string,
  work: Promise<T>,
): Promise<T | undefined> {
  try {
    const value = await work;
    dispatch({ type: ActionType.CLEAR_LOAD_ERROR, key } as any);
    return value;
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    dispatch({ type: ActionType.SET_LOAD_ERROR, key, message } as any);
    return undefined;
  }
}

// A fetch of one record that finds nothing resolves null or undefined. Left alone that reads as "still loading"
// (loadStatus), so reject it: trackLoad then records the error and the boundary shows it with Retry.
export async function requireRecord<T>(work: Promise<T | null | undefined>, what: string): Promise<T> {
  const record = await work;
  if (record === null || record === undefined) throw new Error(`${what} not found`);
  return record;
}

export const useTrackedLoad = () => {
  const dispatch = useDispatch<AppDispatch>();
  return function tracked<T>(key: string, work: Promise<T>) {
    return trackLoad(dispatch, key, work);
  };
};
